import React, { useState, useEffect, useCallback } from 'react';

import {
  DateInputWithConfirm,
  TextareaWithConfirm,
  getDateFromString,
  getFormatDateFromStoreDate,
  getStringFromDate,
  RadioOptionsWithConfirm,
  IQuestion,
  YearInputWithConfirm,
  SelectWithConfirm,
  ContinueButton,
} from 'shared-components';

import { normalizeToFullYear } from '../../../../utilities/formatting';

import { Q } from '../../../../store/student/types';
import { IDate, IStudentVisa } from '../../../../types';
import { previouslyCompletedStudyLevels, PreviousStudyVisas } from 'data';

interface IProps {
  index: number;
  visaType: string;
  level: string;
  year: string;
  isComplete?: boolean;
  incompleteReason?: string;
  isIncompleteRecorded?: boolean;
  isVisaShortened?: boolean;
  shortenedDate?: string;
  onSaveAndClose: (visa: IStudentVisa) => void;
}

const Visas = (props: IProps) => {
  const { index, onSaveAndClose } = props;
  const [visaType, setVisaType] = useState<string>(props.visaType);
  const [level, setLevel] = useState<string>(props.level);
  const [year, setYear] = useState<string>(props.year);
  const [isComplete, setIsComplete] = useState<boolean | undefined>(props.isComplete);
  const [incompleteReason, setIncompleteReason] = useState<string | undefined>(props.incompleteReason);
  const [isIncompleteRecorded, setIsIncompleteRecorded] = useState<boolean | undefined>(props.isIncompleteRecorded);
  const [isVisaShortened, setIsVisaShortened] = useState<boolean | undefined>(props.isVisaShortened);
  const [dateErrorMsg, setStateErrorMsg] = useState<string>('');
  const [shortenedDate, setShortenedDate] = useState<string | undefined>(props.shortenedDate);
  const [date, setDate] = useState<IDate>(getDateFromString(props.shortenedDate));
  const [complete, setComplete] = useState<boolean>(
    props.visaType !== '' &&
      props.level !== '' &&
      props.year !== '' &&
      ((props.isComplete === false &&
        props.incompleteReason !== '' &&
        props.isIncompleteRecorded !== undefined &&
        ((props.isVisaShortened === true && props.shortenedDate !== '') || props.isVisaShortened === false)) ||
        props.isComplete === true),
  );

  const [yearErrorMsg, setYearErrorMsg] = useState<string>('');

  const validateYear = useCallback(() => {
    const currentYear = new Date().getFullYear();
    if (currentYear - Number(year) < 0) {
      setYearErrorMsg('Year must be in the past or current year');
    } else {
      setYearErrorMsg('');
    }
  }, [year]);

  const validateDate = useCallback(() => {
    const { YYYY, DD, MM } = date;
    if (YYYY && DD && MM) {
      const year = normalizeToFullYear(YYYY);
      setStateErrorMsg('');
      if (getFormatDateFromStoreDate(shortenedDate) !== `${DD}/${MM}/${year}`) {
        setShortenedDate(getStringFromDate(date));
      }
    }
  }, [date, shortenedDate]);

  const saveAndClose = () => {
    onSaveAndClose({
      visaType,
      level,
      year,
      isComplete,
      incompleteReason,
      isIncompleteRecorded,
      isVisaShortened,
      shortenedDate,
    });
  };

  const handleOnSaveAndCloseClick = (_event: React.MouseEvent<HTMLButtonElement>): void => {
    saveAndClose();
  };

  useEffect(() => {
    setComplete(
      visaType !== '' &&
        level !== '' &&
        year !== '' &&
        year.length === 4 &&
        yearErrorMsg === '' &&
        ((isComplete === false &&
          incompleteReason !== '' &&
          isIncompleteRecorded !== undefined &&
          ((isVisaShortened === true && props.shortenedDate !== '') || isVisaShortened === false)) ||
          isComplete === true),
    );
  }, [
    visaType,
    level,
    year,
    yearErrorMsg,
    isComplete,
    incompleteReason,
    isIncompleteRecorded,
    isVisaShortened,
    props.shortenedDate,
  ]);

  useEffect(() => {
    if (date?.YYYY?.length === 4) {
      validateDate();
    }
  }, [date, validateDate]);

  useEffect(() => {
    if (year.length === 4) {
      validateYear();
    }
  }, [year, validateYear]);

  return (
    <>
      <SelectWithConfirm
        key={`${Q.PREVIOUS_UK_STUDY_TYPE}-${index}`}
        id={`${Q.PREVIOUS_UK_STUDY_TYPE}-${index}`}
        label={'What type of student visa?'}
        options={PreviousStudyVisas}
        value={visaType}
        onChange={(evt: React.FormEvent<HTMLInputElement>) => setVisaType(evt.currentTarget.value)}
      />

      <SelectWithConfirm
        key={`${Q.PREVIOUS_UK_STUDY_LEVEL_OF_STUDY}-${index}`}
        id={`${Q.PREVIOUS_UK_STUDY_LEVEL_OF_STUDY}-${index}`}
        label={'What was the level of study'}
        options={previouslyCompletedStudyLevels}
        value={level}
        onChange={(evt: React.FormEvent<HTMLInputElement>) => setLevel(evt.currentTarget.value)}
      />

      <YearInputWithConfirm
        key={`${Q.PREVIOUS_UK_STUDY_YEAR_OF_RECEIVED}-${index}`}
        id={`${Q.PREVIOUS_UK_STUDY_YEAR_OF_RECEIVED}-${index}`}
        value={year}
        label={'What was the year you received it?'}
        onChange={(evt: React.FormEvent<HTMLInputElement>) => setYear(evt.currentTarget.value)}
        maxLength={4}
        errorText={yearErrorMsg}
        placeholder={'YYYY'}
      />

      <RadioOptionsWithConfirm
        key={`${Q.PREVIOUS_UK_STUDY_IS_COURSE_COMPLETE}-${index}`}
        id={`${Q.PREVIOUS_UK_STUDY_IS_COURSE_COMPLETE}-${index}`}
        value={isComplete}
        label={'Did you complete this course?'}
        onClick={(question: IQuestion): void => {
          setIsComplete(question.label === 'Yes');
          if (question.label === 'No') {
            setIncompleteReason('');
            setIsIncompleteRecorded(undefined);
            setIsVisaShortened(undefined);
            setDate({
              DD: null,
              MM: null,
              YYYY: null,
            });
            setShortenedDate('');
          }
        }}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: isComplete === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: isComplete === false,
          },
        ]}
      />
      {isComplete === false && (
        <>
          <TextareaWithConfirm
            key={`${Q.PREVIOUS_UK_STUDY_REASON_WHY_NOT_COMPLETE_COURSE}-${index}`}
            id={`${Q.PREVIOUS_UK_STUDY_REASON_WHY_NOT_COMPLETE_COURSE}-${index}`}
            value={incompleteReason}
            label={'Why did you not complete your course?'}
            onChange={(evt: React.FormEvent<HTMLInputElement>) => setIncompleteReason(evt.currentTarget.value)}
          />
          <RadioOptionsWithConfirm
            key={`${Q.PREVIOUS_UK_STUDY_IS_INCOPLETE_RECORDED}-${index}`}
            id={`${Q.PREVIOUS_UK_STUDY_IS_INCOPLETE_RECORDED}-${index}`}
            value={isIncompleteRecorded}
            label={'Does this non-completion appear on your student record?'}
            onClick={(question: IQuestion): void => setIsIncompleteRecorded(question.label === 'Yes')}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: isIncompleteRecorded === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: isIncompleteRecorded === false,
              },
            ]}
          />
          <RadioOptionsWithConfirm
            key={`${Q.PREVIOUS_UK_STUDY_IS_VISA_SHORTENED}-${index}`}
            id={`${Q.PREVIOUS_UK_STUDY_IS_VISA_SHORTENED}-${index}`}
            value={isVisaShortened}
            label={'Was your visa curtailed (shortened) as a result of this?'}
            onClick={(question: IQuestion): void => {
              setIsVisaShortened(question.label === 'Yes');
              if (question.label === 'No') {
                setDate({
                  DD: null,
                  MM: null,
                  YYYY: null,
                });
                setShortenedDate('');
              }
            }}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: isVisaShortened === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: isVisaShortened === false,
              },
            ]}
          />
          {isVisaShortened === true && (
            <DateInputWithConfirm
              key={`${Q.PREVIOUS_UK_STUDY_DATE_VISA_SHORTENED}-${index}`}
              id={`${Q.PREVIOUS_UK_STUDY_DATE_VISA_SHORTENED}-${index}`}
              label={'What was the date that your visa was shortened to?'}
              value={date}
              errorText={dateErrorMsg}
              onChangeDD={(evt: React.FormEvent<HTMLInputElement>) => setDate({ ...date, DD: evt.currentTarget.value })}
              onChangeMM={(evt: React.FormEvent<HTMLInputElement>) => setDate({ ...date, MM: evt.currentTarget.value })}
              onChangeYY={(evt: React.FormEvent<HTMLInputElement>) =>
                setDate({ ...date, YYYY: evt.currentTarget.value })
              }
              onBlur={() => validateDate()}
            />
          )}
        </>
      )}

      <ContinueButton index={index} complete={complete} handleOnSaveAndCloseClick={handleOnSaveAndCloseClick} />
    </>
  );
};

export default Visas;
